import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    currentIndex: { type: Number, default: 0 },
    items: Array,
    submitUrl: String,
    pointsRemaining: Number,
    maxPoints: Number
  }

  static targets = [
    "card", "pointsDisplay", "progressBar", "prevBtn", "nextBtn",
    "itemCounter", "cardTitle", "cardCategory", "cardValue",
    "cardDescription", "cardImage", "favoriteFrame", "cardPreviousBid",
    "bidCountFrame"
  ]

  connect() {
    this.autoSaveTimer = null
    this.pendingAutoSaveItem = null
    this.touchStartX = null
    this.touchStartY = null

    // Touch events for swipe
    if (this.hasCardTarget) {
      this.cardTarget.addEventListener("touchstart", this.handleTouchStart.bind(this), { passive: true })
      this.cardTarget.addEventListener("touchend", this.handleTouchEnd.bind(this), { passive: true })
    }

    this.renderCurrentItem()
  }

  disconnect() {
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer)
  }

  next() {
    this.flushPendingAutoSave()
    if (this.currentIndexValue < this.itemsValue.length - 1) {
      this.currentIndexValue++
      this.renderCurrentItem()
    }
  }

  previous() {
    this.flushPendingAutoSave()
    if (this.currentIndexValue > 0) {
      this.currentIndexValue--
      this.renderCurrentItem()
    }
  }

  flushPendingAutoSave() {
    if (this.autoSaveTimer && this.pendingAutoSaveItem) {
      clearTimeout(this.autoSaveTimer)
      this.autoSaveTimer = null
      this.saveBid(this.pendingAutoSaveItem)
      this.pendingAutoSaveItem = null
    }
  }

  increment() {
    const item = this.itemsValue[this.currentIndexValue]
    if (!item) return

    const step = 5
    const maxAllowed = this.pointsRemainingValue + item.points
    const newPoints = Math.min(item.points + step, maxAllowed)

    this.updateItemPoints(newPoints)
  }

  decrement() {
    const item = this.itemsValue[this.currentIndexValue]
    if (!item) return

    const newPoints = Math.max(item.points - 5, 0)
    this.updateItemPoints(newPoints)
  }

  setPoints() {
    const value = parseInt(this.pointsDisplayTarget.value) || 0
    const item = this.itemsValue[this.currentIndexValue]
    if (!item) return

    const maxAllowed = this.pointsRemainingValue + item.points
    const clamped = Math.max(0, Math.min(value, maxAllowed))

    this.updateItemPoints(clamped)
  }

  updateItemPoints(newPoints) {
    const item = this.itemsValue[this.currentIndexValue]
    const oldPoints = item.points
    const diff = newPoints - oldPoints

    item.points = newPoints
    this.pointsRemainingValue -= diff

    // Update items array (trigger reactivity)
    const items = [...this.itemsValue]
    items[this.currentIndexValue] = { ...item }
    this.itemsValue = items

    this.pointsDisplayTarget.value = newPoints
    this.updatePointsBadge()
    this.updateSubmitAllForm()
    this.scheduleAutoSave(item)
  }

  updatePointsBadge() {
    const badge = document.getElementById("points-remaining-value")
    if (badge) badge.textContent = this.pointsRemainingValue
  }

  updateSubmitAllForm() {
    const container = document.getElementById("submit-all-bids-container")
    if (!container) return

    container.innerHTML = ""
    this.itemsValue.forEach(item => {
      const assetInput = document.createElement("input")
      assetInput.type = "hidden"
      assetInput.name = "bids[][asset_id]"
      assetInput.value = item.assetId

      const pointsInput = document.createElement("input")
      pointsInput.type = "hidden"
      pointsInput.name = "bids[][points]"
      pointsInput.value = item.points

      container.appendChild(assetInput)
      container.appendChild(pointsInput)
    })
  }

  scheduleAutoSave(item) {
    this.pendingAutoSaveItem = item
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer)

    this.autoSaveTimer = setTimeout(() => {
      this.pendingAutoSaveItem = null
      this.saveBid(item)
    }, 500)
  }

  async saveBid(item) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          bids: [{ asset_id: item.assetId, points: item.points }]
        })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.points_remaining !== undefined) {
          this.pointsRemainingValue = data.points_remaining
          this.updatePointsBadge()
        }
      }
    } catch (error) {
      console.error("Auto-save failed:", error)
    }
  }

  renderCurrentItem() {
    const item = this.itemsValue[this.currentIndexValue]
    if (!item) return

    const total = this.itemsValue.length
    const index = this.currentIndexValue

    // Update card content
    if (this.hasCardTitleTarget) this.cardTitleTarget.textContent = item.title
    if (this.hasCardCategoryTarget) this.cardCategoryTarget.textContent = item.category
    if (this.hasCardValueTarget) {
      this.cardValueTarget.textContent = item.estimatedValue ? `$${item.estimatedValue.toLocaleString()}` : ""
    }
    if (this.hasCardDescriptionTarget) {
      this.cardDescriptionTarget.textContent = item.description || ""
    }
    if (this.hasCardImageTarget) {
      if (item.photoUrl) {
        this.cardImageTarget.innerHTML = `<img src="${item.photoUrl}" alt="${item.title}" class="participant-card-photo">`
      } else {
        this.cardImageTarget.innerHTML = '<i class="bi bi-image" style="font-size: 3rem; color: #dee2e6;"></i>'
      }
    }

    // Update points input
    if (this.hasPointsDisplayTarget) this.pointsDisplayTarget.value = item.points

    // Update previous bid hint
    if (this.hasCardPreviousBidTarget) {
      if (item.previousPoints) {
        this.cardPreviousBidTarget.textContent = `Prev: ${item.previousPoints} pts`
        this.cardPreviousBidTarget.style.display = "block"
      } else {
        this.cardPreviousBidTarget.style.display = "none"
      }
    }

    // Update progress
    if (this.hasItemCounterTarget) this.itemCounterTarget.textContent = `${index + 1} / ${total}`
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${((index + 1) / total) * 100}%`

    // Update nav buttons
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = index === 0
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = index === total - 1

    // Update favorite frames visibility
    this.favoriteFrameTargets.forEach(frame => {
      frame.style.display = parseInt(frame.dataset.assetIndex) === index ? "" : "none"
    })

    // Update bid count frames visibility
    this.bidCountFrameTargets.forEach(frame => {
      frame.style.display = parseInt(frame.dataset.assetIndex) === index ? "" : "none"
    })

    // Update submit-all hidden fields
    this.updateSubmitAllForm()
  }

  // Touch swipe handling
  handleTouchStart(event) {
    const touch = event.changedTouches[0]
    this.touchStartX = touch.screenX
    this.touchStartY = touch.screenY
  }

  handleTouchEnd(event) {
    if (this.touchStartX === null) return

    const touch = event.changedTouches[0]
    const deltaX = touch.screenX - this.touchStartX
    const deltaY = touch.screenY - this.touchStartY

    // Horizontal swipe > 50px, vertical < 30px (not a scroll)
    if (Math.abs(deltaX) > 50 && Math.abs(deltaY) < 30) {
      if (deltaX < 0) {
        this.next()
      } else {
        this.previous()
      }
    }

    this.touchStartX = null
    this.touchStartY = null
  }
}
